import { MediaItemCardInGroups } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardInGroups/MediaItemCardInGroups";
import { MediaItemCardDefault } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardDefault/MediaItemCardDefault";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { MediaItemCardUnknown } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardUnknown/MediaItemCardUnknown";
import { MediaItemCardLocalCombination } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardLocalCombination/MediaItemCardLocalCombination";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { Media } from "@coworker/app/src/components/RoomSettings/types/media";
import { AggregatedArticle } from "@coworker/app/src/components/RoomSettings/hooks/useAggregatedArticles";
import {
  Article,
  EdsData,
} from "@coworker/app/src/components/RoomSettings/types/article";
import { ItemBadge } from "@coworker/apprestructured/src/shared/types/ItemBadge";
import { isLocalCombinationArticle } from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import { ArticleType } from "@coworker/types/lib/tasks/constants";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import { MediaItemType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemType";
import { MediaItemCardLCSubItem } from "@coworker/apprestructured/src/media-maintenance/components/item-cards/MediaItemCardLocalCombination/MediaItemCardLCSubItem/MediaItemCardLCSubItem";
import {
  ItemType,
  UNKNOWN_ITEM_NAME,
} from "@coworker/apprestructured/src/shared/constants/itemType";
import LocalCombinationSubArticlePanel from "@coworker/app/src/components/RoomSettings/Molecules/LocalCombinationSubArticlePanel";
import { Item } from "@coworker/apprestructured/src/shared/types/item";

type ItemListViewItemCardBaseProps<T extends Article | AggregatedArticle> = {
  isItemInGroups: boolean;
  itemCardRenderType?: ItemCardRenderType;
  badges: ItemBadge[];
  mediaItemType?: MediaItemType;
  edsData?: EdsData | null;
  article: T;
  mediaItemViewType?: MediaItemViewType;
  itemInfo: Item | null;
  itemInfoLoading: boolean;
  shouldDisplayQuantity?: boolean;
  totalQuantityPieces?: number;
};

type ItemListViewItemCardProps<T extends Article | AggregatedArticle> =
  | (ItemListViewItemCardBaseProps<T> & { showActions?: false; media?: Media })
  | (ItemListViewItemCardBaseProps<T> & { showActions: true; media: Media });

export const ItemListViewItemCard = <T extends Article | AggregatedArticle>(
  props: ItemListViewItemCardProps<T>
) => {
  const {
    isItemInGroups,
    itemCardRenderType = ItemCardRenderType.link,
    badges,
    edsData,
    article,
    showActions = true,
    mediaItemViewType = MediaItemViewType.SIMPLE_ITEM_VIEW,
    mediaItemType,
    itemInfo,
    itemInfoLoading,
    shouldDisplayQuantity = false,
    totalQuantityPieces,
  } = props;
  const actionProps = showActions
    ? ({ showActions: true, media: props.media as Media } as const)
    : ({ showActions: false } as const);
  const isLocalCombinationItem = isLocalCombinationArticle(
    article.productArticleType as ArticleType
  );
  const isUnknownItem =
    (article.productArticleType === ItemType.UNKNOWN ||
      article.productArticleType === ItemType.ARTICLE) &&
    article.itemName.toLowerCase() === UNKNOWN_ITEM_NAME;

  const isLcSubItem = mediaItemType === MediaItemType.LC_SUBITEM;

  return (
    <>
      {itemInfoLoading && <SimpleArticleCardSkeleton />}
      {!itemInfoLoading &&
        itemInfo &&
        (isLcSubItem ? (
          <>
            {article.parentArticle && (
              <LocalCombinationSubArticlePanel
                parentArticle={article.parentArticle}
                noBorderBottom={true}
              />
            )}
            <MediaItemCardLCSubItem
              mediaItemViewType={mediaItemViewType}
              itemInfo={itemInfo}
              article={article}
              edsData={edsData}
              totalQuantityPieces={
                (article.articlesCountInCombination ?? 1) * article.nbrArticles
              }
              itemCardRenderType={
                article.parentArticle
                  ? ItemCardRenderType.link
                  : ItemCardRenderType.static
              }
              shouldDisplayQuantity={true}
            />
          </>
        ) : isItemInGroups ? (
          <MediaItemCardInGroups<AggregatedArticle>
            itemCardRenderType={itemCardRenderType}
            badges={badges}
            edsData={edsData ?? null}
            item={itemInfo}
            article={article as AggregatedArticle}
            {...actionProps}
            mediaItemViewType={
              mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
            }
            shouldDisplayQuantity={shouldDisplayQuantity}
            totalQuantityPieces={totalQuantityPieces}
          />
        ) : (
          <MediaItemCardDefault
            badges={badges}
            itemCardRenderType={itemCardRenderType}
            item={itemInfo}
            article={article}
            {...actionProps}
            edsData={edsData ?? null}
            mediaItemViewType={
              mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
            }
            shouldDisplayQuantity={shouldDisplayQuantity}
            totalQuantityPieces={totalQuantityPieces}
          />
        ))}
      {((!itemInfo && !isLocalCombinationItem) ||
        (itemInfo && isUnknownItem)) && (
        <MediaItemCardUnknown
          itemCardRenderType={itemCardRenderType}
          badges={badges}
          itemNo={article.productArticleId}
          article={article}
          {...actionProps}
          mediaItemViewType={
            mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
          }
        />
      )}
      {isLocalCombinationItem && (
        <MediaItemCardLocalCombination
          isItemInGroups={isItemInGroups}
          shouldDisplayQuantity={shouldDisplayQuantity}
          itemCardRenderType={itemCardRenderType}
          edsData={edsData ?? null}
          article={article}
          badges={badges}
          {...actionProps}
          mediaItemViewType={
            mediaItemViewType ?? MediaItemViewType.AGGREGATED_ITEM_VIEW
          }
          totalQuantityPieces={totalQuantityPieces}
        />
      )}
    </>
  );
};
